import { FC } from 'react';
import { Tr } from '@utils';
import { CarModel, SimulationTask, SimulationVehicle } from '@services';
import { Button, Card, CardProps, Divider, Grid, Stack } from '@mui/material';
import { CarModelInfo, ICarModelInfoProps } from '../CarModelInfo';
import { ISelectableSingleModelnfoProps } from '../SelectableSingleModelInfo';

export interface IVoltaageSuggestsCardProps extends CardProps {
	task: SimulationTask;
	selectedCarModels?: CarModel[];
	vehiclePriceVisible?: boolean;
	onSelectionChange?: ISelectableSingleModelnfoProps['onSelectChange'];
}

export const VoltaageSuggestsCard: FC<IVoltaageSuggestsCardProps> = ({
	task,
	selectedCarModels: selectedVehicles = [],
	vehiclePriceVisible,
	onSelectionChange,
	sx,
	...props
}) => {
	const taskVehicles = (task?.taskVehicles || []).sort((taskVehicle) => {
		if (taskVehicle.proposed) {
			return -1;
		} else if (taskVehicle.notInSuggestedList) {
			return 1;
		}

		return 0;
	});
	const isSelected = ({ carModelId }: SimulationVehicle) => !!selectedVehicles.find((v) => v.id === carModelId);
	const getCardInfoProps = (simulationVehicle: SimulationVehicle): ICarModelInfoProps => ({
		color: 'common.white',
		primaryColor: 'common.black',
		secondaryColor: isSelected(simulationVehicle) ? '#53597c' : 'grey.800',
		mode: 'compare',
		vehicle: task.fleetVehicle,
		simulationVehicle,
		vehiclePriceVisible,
		sx: isSelected(simulationVehicle)
			? {
					backgroundColor: 'transparent',
					backgroundImage: ({ palette }) =>
						`linear-gradient(to bottom, ${palette.primary.main}4f 27%, ${palette.common.black} 135%)`,
			  }
			: {},
	});

	const handleButtonSelect = (simulationVehicle: SimulationVehicle) =>
		onSelectionChange?.(simulationVehicle.carModel, isSelected(simulationVehicle) ? 'deselect' : 'select');

	return (
		<Card variant="outlined" sx={{ flexGrow: 1, backgroundColor: 'common.black', ...sx }} {...props}>
			<Stack direction="column" sx={{ height: 1.0, maxHeight: 1.0, overflowY: 'hidden' }}>
				<Grid
					container
					columns={taskVehicles.length}
					direction="row"
					sx={{ flexGrow: 1, maxHeight: 1.0, overflowY: 'auto', borderTopLeftRadius: 20, borderTopRightRadius: 20 }}
				>
					{taskVehicles.map((simulationVehicle, i) => {
						return (
							<Grid
								key={`proposed-vehicle-${simulationVehicle.carModelId}-${i}`}
								item
								xs={1}
								sx={{ borderRight: (theme) => `1px solid ${theme.palette.grey[600]}` }}
							>
								<CarModelInfo {...getCardInfoProps(simulationVehicle)} />
							</Grid>
						);
					})}
				</Grid>
				<Divider />
				<Grid container direction="row" columns={taskVehicles.length}>
					{taskVehicles.map((simulationVehicle, i) => {
						return (
							<Grid
								key={`proposed-vehicle-select-button-${simulationVehicle.carModelId}-${i}`}
								item
								xs={1}
								sx={{ paddingX: 6, paddingY: 2 }}
							>
								<Button
									variant="light"
									size="small"
									fullWidth
									onClick={() => handleButtonSelect(simulationVehicle)}
									data-testid="conversiton-list-suggestion-card-select-deselect-button"
								>
									<Tr.Portal path={isSelected(simulationVehicle) ? 'remove' : 'select'} />
								</Button>
							</Grid>
						);
					})}
				</Grid>
			</Stack>
		</Card>
	);
};
